/**
 * LiveInventoryEmbed
 * ------------------
 * Compact, dark-theme embed of the live "Hard-to-Find Backorder Inventory"
 * list — designed to sit inside the Hospital Supply Backorder Sourcing page
 * where the surrounding chrome is already dark navy.
 *
 * Reuses the same public endpoint (`GET /api/live-inventory`) as the
 * standalone `/backorders/live-inventory` page.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, Mail, Loader2, PackageSearch, Clock, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Best-effort resolver for the "product name / manufacturer / sku" columns
 * so the per-row "Request Quote" button always has something to prefill,
 * regardless of the exact column headers in the uploaded sheet.
 */
const NAME_KEYS = ["Product Name","Product","Description","Item","Item Description","Product Description","Name"];
const SKU_KEYS  = ["MFR SKU","Manufacturer SKU","SKU","Item #","Item Number","Catalog #","Cat #","Catalog Number","Part #","Part Number"];
const MFR_KEYS  = ["Manufacturer","MFR","Brand","Mfr","Vendor","Supplier"];
const pick = (row, keys) => {
  for (const k of keys) {
    const hit = Object.keys(row).find(h => h.toLowerCase().trim() === k.toLowerCase());
    if (hit && String(row[hit] || "").trim()) return String(row[hit]).trim();
  }
  return "";
};

const PREVIEW_ROWS_DEFAULT = 3;

export default function LiveInventoryEmbed({ variant = "full", theme = "dark", quoteFormPath = "/", quoteFormAnchor = "#quick-quote" }) {
  const compact = variant === "compact";
  const light = theme === "light";
  const previewRows = compact ? 3 : PREVIEW_ROWS_DEFAULT;

  // Palette tokens — one flip switches the whole embed between the dark
  // homepage/Sourcing-page-legacy look and a high-contrast light look.
  const T = light
    ? {
        pill:       "text-amber-900 bg-amber-200 ring-1 ring-amber-400",
        h2:         "text-slate-900",
        h2accent:   "text-cyan-800",
        subtle:     "text-slate-700",
        clockText:  "text-slate-600",
        strongText: "text-slate-900",
        searchBg:   "bg-white border-slate-400 text-slate-900 placeholder:text-slate-500 shadow-sm focus-visible:ring-cyan-500",
        emailBtn:   "border-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-100 font-bold",
        emailPanel: "border border-amber-300 bg-amber-50 shadow-sm",
        loaderText: "text-slate-600",
        emptyBox:   "border-dashed border-slate-300 bg-white shadow-sm",
        emptyH:     "text-slate-900",
        emptyBody:  "text-slate-700",
        emptyLink:  "text-cyan-800 underline",
        tableWrap:  "border border-slate-300 shadow-md bg-white",
        thead:      "bg-slate-900 text-white",
        divider:    "divide-slate-200",
        rowStripe:  ["bg-white", "bg-slate-50"],
        rowHover:   "hover:bg-cyan-50",
        cellText:   "text-slate-800",
        cellDash:   "text-slate-400",
        footerRow:  "bg-slate-100 border-t border-slate-300 text-slate-700",
        showAll:    "text-cyan-800 hover:text-cyan-900 font-semibold",
        openFull:   "text-slate-700 hover:text-slate-900",
      }
    : {
        pill:       "text-amber-300 bg-amber-500/10 ring-1 ring-amber-400/25",
        h2:         "text-white",
        h2accent:   "text-cyan-300",
        subtle:     "text-slate-400",
        clockText:  "text-slate-400",
        strongText: "text-slate-200",
        searchBg:   "bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-cyan-400",
        emailBtn:   "border-amber-400/50 text-amber-200 bg-amber-500/10 hover:bg-amber-500/20 hover:text-white",
        emailPanel: "border border-amber-400/30 bg-amber-500/5 backdrop-blur",
        loaderText: "text-slate-400",
        emptyBox:   "border-dashed border-white/15 bg-white/[0.02]",
        emptyH:     "text-white",
        emptyBody:  "text-slate-400",
        emptyLink:  "text-cyan-300 underline",
        tableWrap:  "border border-white/10 bg-white/[0.02] backdrop-blur",
        thead:      "bg-white/[0.04] text-slate-300",
        divider:    "divide-white/5",
        rowStripe:  ["", ""],
        rowHover:   "hover:bg-cyan-500/5",
        cellText:   "text-slate-200",
        cellDash:   "text-slate-500",
        footerRow:  "border-t border-white/10 text-slate-400",
        showAll:    "text-cyan-300 hover:text-cyan-200 font-semibold",
        openFull:   "text-slate-300 hover:text-white",
      };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ columns: [], rows: [], row_count: 0, updated_at: null, empty: true });
  const [q, setQ] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/live-inventory`);
        setData(r.data || { columns: [], rows: [] });
      } catch (e) {
        toast.error(`Could not load list: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data.rows || [];
    return (data.rows || []).filter(r =>
      Object.values(r).some(v => String(v || "").toLowerCase().includes(term))
    );
  }, [q, data.rows]);
  // Compact previews (homepage + sourcing embed) are locked to `previewRows`
  // — the "View all N items" link routes to the dedicated landing page rather
  // than expanding in place. Only the standalone landing page and the full
  // (non-compact) embed can expand inline via the Show all button.
  const shown = compact
    ? filtered.slice(0, previewRows)
    : (showAll || q ? filtered : filtered.slice(0, previewRows));

  const lastUpdated = data.updated_at
    ? new Date(data.updated_at).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : "Not yet uploaded";

  // Build the deep-link that seeds the backorder form. Default target is the
  // homepage Quick Quote form (`/#quick-quote`) so this works from any page.
  const requestQuoteHref = (row) => {
    const params = new URLSearchParams();
    const name = pick(row, NAME_KEYS);
    const sku  = pick(row, SKU_KEYS);
    const mfr  = pick(row, MFR_KEYS);
    if (name) params.set("prefill_name", name);
    if (sku)  params.set("prefill_sku", sku);
    if (mfr)  params.set("prefill_mfr", mfr);
    return `${quoteFormPath}?${params.toString()}${quoteFormAnchor}`;
  };

  return (
    <div className="relative" data-testid="live-inventory-embed">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <span className={`inline-block text-[11px] font-bold uppercase tracking-[0.2em] ${T.pill} px-3 py-1.5 rounded-full mb-3`}>
            Weekly Deals · Hard-to-Find Live Inventory
          </span>
          <h2 className={`text-3xl md:text-4xl font-extrabold leading-tight ${T.h2}`}>
            Live Backorder List <span className={T.h2accent}>— search any time</span>
          </h2>
          <p className={`mt-2 leading-relaxed text-[15px] max-w-2xl ${T.subtle}`}>
            Every SKU our national supplier partners flag as regional or national backorder — aggregated into one searchable list.
          </p>
          <p className={`mt-2 inline-flex items-center gap-2 text-xs ${T.clockText}`}>
            <Clock className="w-3.5 h-3.5" /> Last updated
            <strong className={T.strongText}>{lastUpdated}</strong>
            {data.row_count ? <span className="ml-1 opacity-70">· {data.row_count} items</span> : null}
          </p>
        </div>
      </div>

      {/* Search + email row */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${light ? "text-slate-500" : "text-slate-500"}`} />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by product name, manufacturer, category, SKU…"
            className={`pl-10 h-12 text-base ${T.searchBg}`}
            data-testid="sourcing-live-inv-search"
          />
        </div>
        <Button
          variant="outline"
          className={`h-12 px-5 ${T.emailBtn}`}
          onClick={() => setEmailOpen(v => !v)}
          data-testid="sourcing-live-inv-email-toggle"
        >
          <Mail className="w-4 h-4 mr-2" />
          Email me the list
        </Button>
      </div>

      {emailOpen && (
        <div className={`mb-5 rounded-xl p-4 ${T.emailPanel}`}>
          <EmailRequestForm onDone={() => setEmailOpen(false)} light={light} />
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className={`flex items-center gap-2 py-14 justify-center ${T.loaderText}`}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading the list…
        </div>
      ) : data.empty ? (
        <div className={`rounded-xl p-10 text-center ${T.emptyBox}`}>
          <PackageSearch className={`w-8 h-8 mx-auto mb-2 ${light ? "text-slate-400" : "text-slate-500"}`} />
          <p className={`font-semibold ${T.emptyH}`}>The list hasn&rsquo;t been uploaded yet.</p>
          <p className={`text-sm mt-1 ${T.emptyBody}`}>
            Check back shortly or email <a className={T.emptyLink} href="mailto:orders@sterilemate.com">orders@sterilemate.com</a> and we&rsquo;ll send the latest one right over.
          </p>
        </div>
      ) : shown.length === 0 ? (
        <div className={`rounded-xl p-10 text-center ${T.emptyBox}`}>
          <p className={T.emptyBody}>No matches for &ldquo;<b className={T.emptyH}>{q}</b>&rdquo;.</p>
        </div>
      ) : (
        <div className={`rounded-xl overflow-hidden ${T.tableWrap}`}>
          <div className="overflow-x-auto">
            {/* Compact preview hides low-signal columns (Notes) and lets the
                browser size columns naturally with table-auto, so Description
                gets the room it needs instead of wrapping into 6-line towers. */}
            {(() => {
              const hideInCompact = new Set(["notes"]);
              const visibleCols = compact
                ? data.columns.filter((c) => !hideInCompact.has(String(c || "").toLowerCase()))
                : data.columns;
              return (
                <table className={`w-full ${compact ? "table-auto" : "table-fixed"} text-xs`} data-testid="sourcing-live-inv-table">
                  <thead className={T.thead}>
                    <tr>
                      {visibleCols.map((col, ci) => (
                        <th
                          key={col}
                          className={`text-left font-bold text-[10px] uppercase tracking-wider px-2 py-2.5 ${
                            !compact && ci === 2 ? "" : "whitespace-nowrap"
                          }`}
                        >
                          {col}
                        </th>
                      ))}
                      <th className="text-right font-bold text-[10px] uppercase tracking-wider px-2 py-2.5 whitespace-nowrap w-[130px]">Action</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${T.divider}`}>
                    {shown.map((r, idx) => (
                      <tr key={idx} className={`${T.rowStripe[idx % 2]} ${T.rowHover} transition-colors align-top`}>
                        {visibleCols.map((col, ci) => {
                          const isDesc = String(col || "").toLowerCase().includes("desc");
                          return (
                            <td
                              key={col}
                              className={`px-2 py-2.5 ${T.cellText} ${
                                compact
                                  ? isDesc
                                    ? "whitespace-normal leading-snug min-w-[240px]"
                                    : "whitespace-nowrap"
                                  : ci === 2
                                  ? "whitespace-normal leading-snug"
                                  : "whitespace-nowrap truncate"
                              }`}
                              title={r[col]}
                            >
                              {r[col] || <span className={T.cellDash}>—</span>}
                            </td>
                          );
                        })}
                        <td className="px-2 py-2.5 text-right whitespace-nowrap">
                          <a
                            href={requestQuoteHref(r)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-amber-500 text-slate-900 text-[11px] font-black hover:bg-amber-400 shadow-sm transition-colors"
                            data-testid={`live-inv-request-quote-${idx}`}
                            title="Add this item to the backorder request form"
                          >
                            <Send className="w-3 h-3" />
                            Request a Quote
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
          <div className={`px-4 py-3 text-xs flex flex-wrap items-center justify-between gap-2 ${T.footerRow}`}>
            <span>
              Showing {shown.length} of {data.row_count} items
              {compact ? " (preview) " : (!showAll && !q && filtered.length > previewRows ? " (preview) " : "")}
            </span>
            <div className="flex items-center gap-3">
              {compact ? (
                // Homepage / sourcing preview: never expand in-place — always
                // route buyers to the dedicated full-list landing page.
                <Link
                  to="/backorders/live-inventory"
                  className={T.showAll}
                  data-testid="live-inv-open-full"
                >
                  View all {data.row_count} items →
                </Link>
              ) : (
                <>
                  {!showAll && !q && filtered.length > previewRows && (
                    <button
                      onClick={() => setShowAll(true)}
                      className={T.showAll}
                      data-testid="sourcing-live-inv-show-all"
                    >
                      Show all {filtered.length} items →
                    </button>
                  )}
                  <Link
                    to="/backorders/live-inventory"
                    className={`inline-flex items-center gap-1 ${T.openFull}`}
                    data-testid="live-inv-open-full-page"
                  >
                    Open full page <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmailRequestForm({ onDone, light = false }) {
  const [form, setForm] = useState({ email: "", name: "", hospital: "", note: "" });
  const [saving, setSaving] = useState(false);
  const inputCls = light
    ? "bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
    : "bg-white/5 border-white/10 text-white placeholder:text-slate-500";
  const cancelCls = light ? "text-slate-700 hover:text-slate-900" : "text-slate-300 hover:text-white";
  const submit = async () => {
    if (!form.email) {
      toast.error("Add your email so we know where to send it.");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/live-inventory/request`, form);
      toast.success(r.data?.message || "Request sent.");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Input placeholder="Your email *" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inputCls} data-testid="sourcing-live-inv-email-email" />
      <Input placeholder="Your name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inputCls} data-testid="sourcing-live-inv-email-name" />
      <Input placeholder="Hospital / health system" value={form.hospital} onChange={e => setForm(f => ({ ...f, hospital: e.target.value }))} className={inputCls} data-testid="sourcing-live-inv-email-hospital" />
      <Input placeholder="Categories of interest (surgical, anesthesia, wound care…)" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} className={inputCls} data-testid="sourcing-live-inv-email-note" />
      <div className="md:col-span-2 flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone} disabled={saving} className={cancelCls}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" data-testid="sourcing-live-inv-email-submit">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
          Send me the list
        </Button>
      </div>
    </div>
  );
}
